import { useEffect, useMemo, useRef, useState } from "react";
import type { IEmployee } from "../../employees/Interfaces/IEmployeeInterface";

import styles from "./InviteSelector.module.css";
import Down from "../../../assets/icons/Down.svg";
import Edit from "../../../assets/icons/edit_icon.svg";

interface InviteSelectorProps {
  employees: IEmployee[];
  label: string;
  selected: string;
  onSelect: (val: string) => void;
}

export default function InviteSelector({
  employees,
  label,
  selected,
  onSelect,
}: InviteSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const selectedEmployee = useMemo(
    () => employees.find((emp) => emp.id === selected) ?? null,
    [employees, selected]
  );

  const normalizedQuery = searchTerm.trim().toLowerCase();
  const filteredEmployees = useMemo(() => {
    if (!normalizedQuery) {
      return employees;
    }

    return employees.filter((emp) => {
      const name = emp.name?.toLowerCase() ?? "";
      const area = emp.area?.toLowerCase() ?? "";

      return name.includes(normalizedQuery) || area.includes(normalizedQuery);
    });
  }, [employees, normalizedQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
    setSearchTerm("");
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleSelect = (employee: IEmployee) => {
    onSelect(employee.id);
    setIsOpen(false);
    setSearchTerm("");
  };

  const getInitials = (name: string) => {
    if (!name) return "";

    const [first = "", second = ""] = name.trim().split(/\s+/).slice(0, 2);

    return `${first.charAt(0)}${second.charAt(0)}`.toUpperCase();
  };

  const inputValue = isOpen ? searchTerm : selectedEmployee?.name ?? "";

  const handleEditSelection = () => {
    setSearchTerm("");
    setIsOpen(true);
  };

  const showSelectedCard = Boolean(selectedEmployee && !isOpen);

  return (
    <div className={styles.container} ref={containerRef}>
      <label className={styles.label}>{label}</label>

      <div className={styles.dropdown}>
        {showSelectedCard ? (
          <div className={styles.selectedCard}>
            <div className={styles.selectedInfo}>
              <span className={styles.selectedName}>
                {selectedEmployee?.name}
              </span>
              <span className={styles.selectedDescription}>
                {selectedEmployee?.areaName || "-"}
              </span>
            </div>

            <button
              type="button"
              className={styles.editButton}
              onClick={handleEditSelection}
            >
              <img src={Edit} alt="Edit" />
              <span>Editar colega</span>
            </button>
          </div>
        ) : (
          <>
            <div className={styles.inputWrapper}>
              <input
                ref={inputRef}
                type="text"
                className={styles.searchInput}
                placeholder="Pesquisa por nome ou departamento"
                value={inputValue}
                onFocus={handleFocus}
                onChange={(event) => {
                  setIsOpen(true);
                  setSearchTerm(event.target.value);
                }}
              />

              <button
                type="button"
                className={styles.toggleButton}
                aria-label={isOpen ? "Fechar lista" : "Abrir lista"}
                onClick={handleToggle}
              >
                <img
                  className={`${isOpen ? styles.arrowOpen : ""}`}
                  src={Down}
                  alt="Arrow down"
                />
              </button>
            </div>

            {isOpen && (
              <div className={styles.menu}>
                {filteredEmployees.length === 0 ? (
                  <div className={styles.emptyState}>Sem resultados</div>
                ) : (
                  <ul className={styles.optionList}>
                    {filteredEmployees.map((employee) => {
                      const isSelected = employee.id === selected;

                      return (
                        <li
                          key={employee.id}
                          className={`${styles.option} ${
                            isSelected ? styles.optionSelected : ""
                          }`}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => handleSelect(employee)}
                        >
                          <div className={styles.avatar}>
                            {employee.photoBase64 ? (
                              <img
                                src={`data:image/jpeg;base64,${atob(
                                  employee.photoBase64
                                )}`}
                                alt={employee.name}
                                style={{ width: "32px", height: "32px" }}
                              />
                            ) : (
                              getInitials(employee.name)
                            )}
                          </div>

                          <div className={styles.optionContent}>
                            <span className={styles.optionName}>
                              {employee.name}
                            </span>
                            <span className={styles.optionDescription}>
                              {employee.areaName || "-"}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
